import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import '../components/DonationForm/DonationForm.css';
import { CountryOption, StateOption } from '../types/donation';
import { donationService } from '../services/api';

const PER_ATTENDEE = 20;

type AnnRegFormData = {
  // Personal Details (email moved to payment section)
  firstName: string;
  lastName: string;
  phoneNo: string;
  address: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;

  // Page specific
  willAttend: boolean;
  attendees: number;

  // Payment section
  email: string;
  donationAmount: string; // optional extra donation
  totalAmount: string; // computed total = donationAmount + attendees * PER_ATTENDEE
  cardNumber: string;
  cvv: string;
  expirationDate: string;
};

const AnnReg: React.FC = () => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  // Load countries and default state similar to Donation page
  useEffect(() => {
    const fetchCountries = async () => {
      const countryData = await donationService.getCountries();
      setCountries(countryData);
      const usa = countryData.find(c => /^(united states|usa|united states of america)$/i.test(c.name));
      const selected = usa || countryData[0];
      if (selected) {
        const cid = Number.parseInt(String(selected.value), 10) || Number(selected.id);
        const defaultStates = await donationService.getStatesByCountry(cid);
        setStates(defaultStates);
      }
    };
    fetchCountries();
  }, []);

  // reCAPTCHA v2 render (dedicated container id for AnnReg)
  useEffect(() => {
    const ensureScript = (): Promise<void> => {
      return new Promise((resolve) => {
        const w = window as any;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window as any;
      const el = document.getElementById('recaptcha-container-annreg');
      if (!el) return;
      if (recaptchaWidgetId !== null) return;
      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) { setTimeout(pollUntilReady, 150); return; }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') { setTimeout(doRender, 150); return; }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch {
            setTimeout(doRender, 200);
          }
        };
        if (typeof api.ready === 'function') api.ready(doRender); else setTimeout(doRender, 0);
      };
      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => { if (!mounted) return; setTimeout(renderWidget, 0); });
    return () => { mounted = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  const initialValues: AnnRegFormData = {
    // Personal Details
    firstName: '',
    lastName: '',
    phoneNo: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    // Page specific
    willAttend: false,
    attendees: 1,
    // Payment section
    email: '',
    donationAmount: '',
    totalAmount: '',
    cardNumber: '',
    cvv: '',
    expirationDate: '',
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    phoneNo: Yup.string().required('Phone Number is required'),
    address: Yup.string().required('Address is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    postalCode: Yup.string().required('Postal Code/ZIP is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    willAttend: Yup.boolean().oneOf([true], 'Please confirm you will attend').required(),
    attendees: Yup.number().typeError('Attendees must be a number').min(1, 'At least 1 attendee').required('Attendees is required'),
    donationAmount: Yup.string()
      .test('valid-donation', 'Enter a valid amount (e.g., 10 or 10.50)', (val) => !val || /^\d+(?:\.\d{1,2})?$/.test(val))
      .test('donation-gt-zero', 'Amount must be greater than 0', (val) => !val || Number(val) > 0),
    totalAmount: Yup.string()
      .matches(/^\d+(?:\.\d{1,2})?$/, 'Enter a valid total amount')
      .test('total-gt-zero', 'Total must be greater than 0', (val) => { const n = Number(val); return Number.isFinite(n) && n > 0; })
      .required('Total Amount is required'),
    cardNumber: Yup.string().required('Card Number is required')
      .test('len', 'Enter a valid card number (13–19 digits)', (val) => /^[0-9]{13,19}$/.test((val || '').replace(/\D/g, ''))),
    cvv: Yup.string().required('CVV/CVC is required').matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits'),
    expirationDate: Yup.string().required('Expiration Date is required').matches(/^(0[1-9]|1[0-2])\/(20[2-9][0-9])$/, 'Format must be MM/YYYY'),
  });

  const handleCountryChange = async (selectedCountry: any, formikProps: any) => {
    if (!selectedCountry) return;
    formikProps.setFieldValue('country', selectedCountry.value);
    formikProps.setFieldValue('state', '');
    const stateData = await donationService.getStatesByCountry(Number(selectedCountry.value));
    setStates(stateData);
    if (stateData[0]) {
      formikProps.setFieldValue('state', String(stateData[0].value));
    }
  };

  const handleSubmit = async (values: AnnRegFormData, formikHelpers?: any) => {
    if (values.willAttend !== true) {
      Swal.fire({ icon: 'warning', title: 'Please confirm attendance', text: 'Select "I will attend" and specify attendees to continue.' });
      return;
    }
    setLoading(true);

    if (!recaptchaToken) {
      setLoading(false);
      Swal.fire({ icon: 'warning', title: 'reCAPTCHA', text: 'Please complete reCAPTCHA before submitting.' });
      return;
    }

    const verify = await donationService.verifyRecaptcha(recaptchaToken);
    if (!verify || verify.success !== true) {
      setLoading(false);
      Swal.fire({ icon: 'error', title: 'reCAPTCHA failed', text: 'Please try again.' });
      try {
        const w = window as any;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch { }
      return;
    }

    try {
      const countryLabel = countries.find(c => String(c.value) === String(values.country))?.label || '';
      const stateLabel = states.find(s => String(s.value) === String(values.state))?.label || '';

      const description = `Anniversary Registration for ${values.firstName} ${values.lastName}. Attendees: ${values.attendees}. Total Amount: ${values.totalAmount}`;
      const payload: any = {
        app: 'Anniversary',
        entity: 'rms',
        paymentType: 'cc',
        customerEmail: values.email,
        orderid: '0',
        amount: String(values.totalAmount),
        description,
        customer: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phoneNo.replace(/\D/g, ''),
          company: 'string',
          notes: description,
          customerAccount: 'string',
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
          shippingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
        },
        creditcard: {
          creditCardNumber: values.cardNumber,
          expirationDate: values.expirationDate,
          issuer: 'string',
          billingZipCode: values.postalCode,
          customerId: 'string',
          cvv: values.cvv,
        },
        ach: {
          isCheckingAccount: 'string',
          routingNumber: 'string',
          accountNumber: 'string',
          bankName: 'string',
          customerId: 'string',
          isDefault: 'string',
        },
        recurr: { // not used
          paymentAmount: 'string', accountId: 'string', startDate: 'string', executionFrequencyType: 'string', executionFrequencyParameter: 'string', endDate: 'string', paymentSubType: 'string', invoiceNumber: 'string', firstPaymentAmount: 'string', firstPaymentDate: 'string', description: 'string'
        }
      };

      const paymentResp: any = await donationService.authNetOneTimePayment(payload);
      const respMessages = (
        paymentResp?.messages ||
        paymentResp?.Messages ||
        paymentResp?.createTransactionResponse?.messages ||
        paymentResp?.message ||
        paymentResp?.Message ||
        {}
      );
      const resultCode = String(
        respMessages?.resultCode ?? paymentResp?.resultCode ?? ''
      );
      const msgArray = Array.isArray((respMessages as any)?.message)
        ? (respMessages as any).message
        : ((respMessages as any)?.message ? [(respMessages as any).message] : []);
      const messageCode = (msgArray[0]?.code) || (paymentResp?.createTransactionResponse?.messages?.message?.code);
      let messageText = '';
      if (typeof respMessages === 'string') {
        messageText = respMessages;
      } else {
        messageText = (msgArray[0]?.text || msgArray[0]?.description) || '';
      }
      // Fallbacks for top-level string messages
      if (!messageText && typeof (paymentResp as any)?.Message === 'string') {
        messageText = (paymentResp as any).Message;
      }
      if (!messageText && typeof (paymentResp as any)?.message === 'string') {
        messageText = (paymentResp as any).message;
      }

      const ok = (
        paymentResp?.success === true ||
        paymentResp?.isSuccess === true ||
        paymentResp?.status === 'OK' ||
        ['ok', 'OK', 'Ok'].includes(resultCode) ||
        messageCode === 'I00001'
      );

      if (ok) {
        await donationService.sendEmailMessage(
          {
            Email: values.email,
            ContactNo: values.phoneNo,
            UserName: `${values.firstName} ${values.lastName}`.trim(),
            DonationDate: new Date().toISOString(),
            Amount: String(values.totalAmount),
            Address: values.address,
          },
          { appName: 'Anniversary', templateName: 'RMSDonation', orgId: 3 }
        );
        Swal.fire({ icon: 'success', title: 'Thank you!', text: 'Your payment was successful and a confirmation email has been sent.', confirmButtonText: 'OK' })
          .then(() => {
            if (typeof window !== 'undefined') {
              window.location.assign('/thankyou');
            }
          });
        if (formikHelpers && typeof formikHelpers.resetForm === 'function') formikHelpers.resetForm();
        try {
          const w = window as any; if (recaptchaWidgetId !== null && w.grecaptcha) { w.grecaptcha.reset(recaptchaWidgetId); setRecaptchaToken(''); }
        } catch { }
      } else {
        Swal.fire({ icon: 'error', title: 'Payment failed', text: 'Please verify your details and try again.' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while processing your payment.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="page-loader">
          <div className="spinner"></div>
          <p>Please wait while we process your payment...</p>
        </div>
      )}
      <Container className="donation-form-container">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {(formikProps) => {
            return (
              <Form>
                <Row>
                  <Col md={6}>
                    <Card className="mb-4 elevated-card">
                      <Card.Header>Please Enter Personal Details</Card.Header>
                      <Card.Body>
                        <div className="form-check mb-1">
                          <Field name="willAttend">
                            {({ field }: any) => (
                              <input
                                {...field}
                                type="checkbox"
                                id="willAttend"
                                className="form-check-input"
                                checked={field.value}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  formikProps.setFieldValue('willAttend', checked);
                                  const donation = Number(formikProps.values.donationAmount || 0);
                                  const tickets = checked ? Math.max(1, Number(formikProps.values.attendees || 1)) * PER_ATTENDEE : 0;
                                  const total = donation + tickets;
                                  formikProps.setFieldValue('totalAmount', total ? String(total.toFixed(2)) : '');
                                }}
                              />
                            )}
                          </Field>
                          <label className="form-check-label ms-2" htmlFor="willAttend">I will attend</label>
                        </div>
                        <ErrorMessage name="willAttend" component="div" className="text-danger mb-2" />

                        {formikProps.values.willAttend && (
                          <Row className="align-items-center mb-3">
                            <Col sm="12">
                              <label htmlFor="attendees" className="form-label mb-1">Number of Attendees</label>
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <Button
                                  type="button"
                                  variant="outline-secondary"
                                  onClick={() => {
                                    const next = Math.max(1, Number(formikProps.values.attendees) - 1);
                                    formikProps.setFieldValue('attendees', next);
                                    const donation = Number(formikProps.values.donationAmount || 0);
                                    const total = (formikProps.values.willAttend ? next * PER_ATTENDEE : 0) + donation;
                                    formikProps.setFieldValue('totalAmount', String(total.toFixed(2)));
                                  }}
                                  aria-label="decrement attendees"
                                  disabled={Number(formikProps.values.attendees) <= 1}
                                >
                                  −
                                </Button>
                                <Field name="attendees">
                                  {({ field }: any) => (
                                    <input
                                      {...field}
                                      id="attendees"
                                      type="number"
                                      min={1}
                                      step={1}
                                      className="form-control"
                                      style={{ maxWidth: 120 }}
                                      inputMode="numeric"
                                      onChange={(e) => {
                                        const v = Math.max(1, Number(e.target.value || 1));
                                        formikProps.setFieldValue('attendees', v);
                                        const donation = Number(formikProps.values.donationAmount || 0);
                                        const total = (formikProps.values.willAttend ? v * PER_ATTENDEE : 0) + donation;
                                        formikProps.setFieldValue('totalAmount', String(total.toFixed(2)));
                                      }}
                                    />
                                  )}
                                </Field>
                                <Button
                                  type="button"
                                  variant="outline-secondary"
                                  onClick={() => {
                                    const next = Math.max(1, Number(formikProps.values.attendees) + 1);
                                    formikProps.setFieldValue('attendees', next);
                                    const donation = Number(formikProps.values.donationAmount || 0);
                                    const total = (formikProps.values.willAttend ? next * PER_ATTENDEE : 0) + donation;
                                    formikProps.setFieldValue('totalAmount', String(total.toFixed(2)));
                                  }}
                                  aria-label="increment attendees"
                                >
                                  +
                                </Button>
                              </div>
                              <div className="text-muted mt-2">Ticket Price: ${PER_ATTENDEE} per person</div>
                            </Col>
                          </Row>
                        )}

                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="firstName">First Name *</label>
                            <Field name="firstName" type="text" className="form-control" placeholder="First Name" />
                            <ErrorMessage name="firstName" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="lastName">Last Name *</label>
                            <Field name="lastName" type="text" className="form-control" placeholder="Last Name" />
                            <ErrorMessage name="lastName" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="phoneNo">Phone No *</label>
                            <Field name="phoneNo" type="text" className="form-control" placeholder="Phone No" />
                            <ErrorMessage name="phoneNo" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="address">Address *</label>
                            <Field name="address" as="textarea" className="form-control" placeholder="Address" />
                            <ErrorMessage name="address" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="email">Email *</label>
                            <Field name="email" type="email" className="form-control" placeholder="Email" />
                            <ErrorMessage name="email" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="country">Select Country *</label>
                            <Select
                              options={countries.map(c => ({ value: c.value, label: c.label, id: c.id }))}
                              value={(formikProps.values.country && countries.length) ? { value: formikProps.values.country, label: countries.find(c => c.value === formikProps.values.country)?.label || '' } : null}
                              onChange={(opt) => handleCountryChange(opt, formikProps)}
                              className="basic-select"
                              classNamePrefix="select"
                              placeholder="Select Country"
                            />
                            <ErrorMessage name="country" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="state">Select State *</label>
                            <Select
                              options={states.map(s => ({ value: s.value, label: s.label }))}
                              value={(formikProps.values.state && states.length) ? { value: formikProps.values.state, label: states.find(s => s.value === formikProps.values.state)?.label || '' } : null}
                              onChange={(opt) => { if (opt) formikProps.setFieldValue('state', opt.value); }}
                              isDisabled={states.length === 0}
                              className="basic-select"
                              classNamePrefix="select"
                              placeholder="Select State"
                            />
                            <ErrorMessage name="state" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="city">City *</label>
                            <Field name="city" type="text" className="form-control" placeholder="City" />
                            <ErrorMessage name="city" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="postalCode">Postal Code/ZIP Code *</label>
                            <Field name="postalCode" type="text" className="form-control" placeholder="Postal Code/ZIP Code" />
                            <ErrorMessage name="postalCode" component="div" className="text-danger" />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="mb-4 elevated-card">
                      <Card.Header>Please Enter Credit Card Details</Card.Header>
                      <Card.Body>
                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="donationAmount">Optional Donation Amount</label>
                            <Field name="donationAmount">
                              {({ field }: any) => (
                                <input
                                  {...field}
                                  type="text"
                                  className="form-control"
                                  placeholder="I would like to support Yugal Kunj"
                                  inputMode="decimal"
                                  onChange={(e) => {
                                    let v = e.target.value.replace(/[^0-9.]/g, '');
                                    const parts = v.split('.');
                                    if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
                                    const [intp, decp = ''] = v.split('.');
                                    const v2 = decp.length ? `${intp}.${decp.slice(0, 2)}` : intp;
                                    formikProps.setFieldValue('donationAmount', v2);
                                    const donation = Number(v2 || 0);
                                    const tickets = formikProps.values.willAttend ? Number(formikProps.values.attendees || 1) * PER_ATTENDEE : 0;
                                    const total = donation + tickets;
                                    formikProps.setFieldValue('totalAmount', String(total.toFixed(2)));
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage name="donationAmount" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="totalAmount">Total Amount</label>
                            <Field name="totalAmount">
                              {({ field }: any) => (
                                <input {...field} type="text" className="form-control" readOnly placeholder="Total Amount" />
                              )}
                            </Field>
                            <ErrorMessage name="totalAmount" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={12}>
                            <label htmlFor="cardNumber">Card Number (13–19 digits) *</label>
                            <Field name="cardNumber">
                              {({ field }: any) => (
                                <input {...field} type="text" className="form-control" placeholder="Enter card number" inputMode="numeric" maxLength={19}
                                  onChange={(e) => { const digits = e.target.value.replace(/\D/g, '').slice(0, 19); formikProps.setFieldValue('cardNumber', digits); }} />
                              )}
                            </Field>
                            <ErrorMessage name="cardNumber" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="cvv">CVV/CVC *</label>
                            <Field name="cvv" type="text" className="form-control" placeholder="CVV" />
                            <ErrorMessage name="cvv" component="div" className="text-danger" />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="expirationDate">Expiration MM/YYYY *</label>
                            <Field name="expirationDate" type="text" className="form-control" placeholder="MM/YYYY" />
                            <ErrorMessage name="expirationDate" component="div" className="text-danger" />
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <div id="recaptcha-container-annreg"></div>
                          {!recaptchaToken && (
                            <div className="text-danger mt-2">Please complete reCAPTCHA</div>
                          )}
                        </div>

                        {/* <Button type="submit" variant="success" disabled={loading}>
                          {loading ? (<><Spinner animation="border" size="sm" className="me-2" />Submitting...</>) : 'Submit'}
                        </Button> */}
                        <Button type="submit" variant="success" disabled={loading}>
                          Submit
                        </Button>

                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </>
  );
};

export default AnnReg;
